// Utils
import { UserUtils } from 'utils';
import ROLES from 'core/Authorization/roles';
import GENERAL from 'utils/constants/general';

const { DIVISION_ID } = GENERAL;

const getEntityContext = profile => {
	const entityId = profile?.entity?.id;
	const mainEntityId = profile?.organization?.settings?.mainEntity;
	const isKnown =
		entityId !== undefined &&
		entityId !== null &&
		mainEntityId !== undefined &&
		mainEntityId !== null;

	return {
		isKnown,
		isMainEntity: isKnown && UserUtils.isUserBelongsMainEntity(profile),
	};
};

export const canOpenContractorPaymentSummary = ({ profile, isInvoice }) => {
	const { isKnown, isMainEntity } = getEntityContext(profile);

	return !!isInvoice && isKnown && !isMainEntity;
};

export const canShowLegacyBillingSummaryAction = ({ profile, isInvoice }) => {
	if (!isInvoice) return false;

	const { isKnown, isMainEntity } = getEntityContext(profile);
	return isKnown && isMainEntity;
};

export const hasGponPricingSignalsImportEnabled = project =>
	project?.props?.billings?.actions?.payments?.importGponPricingSignals === true;

export const canImportGponPricingSignals = ({ profile, isInvoice, project }) => {
	if (isInvoice) return false;

	const { isKnown, isMainEntity } = getEntityContext(profile);
	if (!isKnown || !isMainEntity) return false;
	if (!hasGponPricingSignalsImportEnabled(project)) return false;

	return hasCanonicalRole(profile?.user?.roles, ROLES.PAYMENT.L2_MANAGER);
};

const sameId = (left, right) =>
	left !== undefined &&
	left !== null &&
	right !== undefined &&
	right !== null &&
	String(left) === String(right);

const hasCanonicalRole = (roles, roleId) => {
	if (!roles || typeof roles !== 'object' || Array.isArray(roles)) return false;

	return (
		Object.prototype.hasOwnProperty.call(roles, roleId) &&
		roles[roleId] === true
	);
};

export const getPaymentSignatureCapability = ({
	profile,
	payment = {},
} = {}) => {
	const userId = profile?.user?.id;
	const userDivisionId = profile?.user?.division_id;
	const entityId = profile?.entity?.id;
	const roles = profile?.user?.roles;
	const hasAbsentSignatureCollection = payment.signed == null;
	const hasSignatureCollection = Array.isArray(payment.signed);
	const signatures = hasSignatureCollection ? payment.signed : [];
	const hasWellFormedSignatureFlags =
		hasSignatureCollection &&
		signatures.every(
			signature =>
				signature &&
				typeof signature === 'object' &&
				['agreed', 'released', 'paid'].every(
					flag =>
						signature[flag] === undefined ||
						typeof signature[flag] === 'boolean',
				),
		);
	const hasAgreedSignature = signatures.some(
		signature => signature?.agreed === true,
	);
	const hasTerminalSignature = signatures.some(
		signature => signature?.released === true || signature?.paid === true,
	);
	const alreadySigned = signatures.some(signature =>
		sameId(signature.user_id, userId),
	);
	const isOrigin = sameId(payment.entity_id, entityId);
	const isSelfEntityPayment = sameId(payment.entity_id, payment.paid_to);
	const isRecipientCompany = sameId(payment.paid_to, entityId);
	const isRecipientTechnician =
		sameId(userDivisionId, DIVISION_ID.EXP) && sameId(payment.paid_to, userId);
	const hasCompleteIdentity =
		userId !== undefined &&
		userId !== null &&
		userDivisionId !== undefined &&
		userDivisionId !== null &&
		entityId !== undefined &&
		entityId !== null &&
		payment.entity_id !== undefined &&
		payment.entity_id !== null &&
		payment.paid_to !== undefined &&
		payment.paid_to !== null;
	const canAgreeTechnician =
		isRecipientTechnician && hasCanonicalRole(roles, ROLES.EXPERT.L1_EXPERT);
	const canAgreeCompany =
		!isRecipientTechnician &&
		isRecipientCompany &&
		!isSelfEntityPayment &&
		hasCanonicalRole(roles, ROLES.PAYMENT.CHARGE_MANAGER);
	const canAgree = canAgreeCompany || canAgreeTechnician;
	const canRelease =
		!isRecipientTechnician &&
		!isRecipientCompany &&
		isOrigin &&
		hasCanonicalRole(roles, ROLES.PAYMENT.L2_MANAGER);
	const canAgreeInCurrentState =
		canAgree &&
		payment.state === 'ready' &&
		(hasAbsentSignatureCollection || hasWellFormedSignatureFlags) &&
		!hasAgreedSignature &&
		!hasTerminalSignature;
	const canReleaseInCurrentState =
		canRelease &&
		payment.state === 'agreed' &&
		hasWellFormedSignatureFlags &&
		hasAgreedSignature &&
		!hasTerminalSignature;
	const canSign = Boolean(
		hasCompleteIdentity &&
			!alreadySigned &&
			(canAgreeInCurrentState || canReleaseInCurrentState),
	);

	return {
		alreadySigned,
		canSign,
		actionLabel: canSign
			? canAgreeInCurrentState
				? 'Aceptar'
				: 'Firmar'
			: null,
	};
};
